<template>
  <Head :title="news.data.title">
    <meta property="og:title" :content="news.data.title" />
    <meta property="og:description" :content="description" />
    <meta property="og:image" :content="primaryImage" />
    <meta property="og:url" :content="canonicalUrl" />
    <meta property="og:type" content="article" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" :content="news.data.title" />
    <meta name="twitter:description" :content="description" />
    <meta name="twitter:image" :content="primaryImage" />
    <link rel="canonical" :href="canonicalUrl" />
  </Head>

  <Container :judul="news.data.title">
    <template #header>
      <HeaderFe />
    </template>

    <template v-slot:default>
      <main class="bg-gray-50 min-h-screen pt-28">
        <div class="max-w-[1170px] mx-auto px-4 sm:px-6 xl:px-0 py-10">
          <div class="flex flex-col gap-8 lg:flex-row lg:items-start">

            <!-- ── Article ───────────────────────────────────── -->
            <article class="flex-1 min-w-0">
              <div class="bg-white rounded-xl shadow-sm overflow-hidden">

                <!-- Hero image -->
                <div
                  v-if="heroImage"
                  class="w-full aspect-[16/7] overflow-hidden bg-gray-100"
                >
                  <img
                    :src="heroImage"
                    :alt="news.data.title"
                    class="w-full h-full object-cover"
                  />
                </div>

                <div class="p-6 sm:p-8 md:p-10">
                  <!-- Title -->
                  <h1 class="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl mb-4">
                    {{ news.data.title }}
                  </h1>

                  <!-- Meta bar -->
                  <div class="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-400 pb-5 border-b border-gray-100">
                    <span v-if="news.data.author" class="flex items-center gap-1.5">
                      <i class="fa-regular fa-user"></i>
                      <span class="font-medium text-gray-600">{{ news.data.author }}</span>
                    </span>
                    <span class="flex items-center gap-1.5">
                      <i class="fa-regular fa-calendar"></i>
                      {{ formatTanggal(news.data.publish_date) }}
                    </span>
                    <span class="flex items-center gap-1.5 ml-auto">
                      <i class="fa-regular fa-eye"></i>
                      {{ news.data.formatted_view_count || '0' }} kali dibaca
                    </span>
                  </div>

                  <!-- Content -->
                  <div
                    v-html="news.data.content"
                    class="mt-6 news-detail-content"
                  ></div>

                  <!-- Footer: tags + share -->
                  <div class="mt-8 pt-6 border-t border-gray-100 flex flex-col sm:flex-row sm:items-start gap-4">
                    <!-- Tags -->
                    <div class="flex-1">
                      <div v-if="news.data.tags && news.data.tags.length" class="flex flex-wrap gap-2">
                        <span
                          v-for="tag in news.data.tags"
                          :key="tag"
                          class="px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-full border border-blue-100"
                        >
                          #{{ tag }}
                        </span>
                      </div>
                    </div>

                    <!-- Share -->
                    <div class="flex items-center gap-3 flex-shrink-0">
                      <span class="text-xs text-gray-400">Bagikan:</span>
                      <a
                        :href="facebookShareUrl"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        class="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm"
                        aria-label="Bagikan ke Facebook"
                      >
                        <i class="fa-brands fa-facebook-f"></i>
                      </a>
                      <a
                        :href="whatsappShareUrl"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        class="w-8 h-8 flex items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors text-sm"
                        aria-label="Bagikan ke WhatsApp"
                      >
                        <i class="fa-brands fa-whatsapp"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </article>

            <!-- ── Sidebar ─────────────────────────────────── -->
            <aside class="w-full lg:w-72 flex-shrink-0 space-y-6">

              <!-- Search -->
              <div class="bg-white rounded-xl shadow-sm p-4">
                <div class="relative">
                  <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
                  <input
                    v-model="search"
                    type="text"
                    placeholder="Cari berita..."
                    class="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <!-- Other news -->
              <div class="bg-white rounded-xl shadow-sm p-4">
                <h3 class="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-100">
                  Berita Lainnya
                </h3>
                <ul class="space-y-4">
                  <li
                    v-for="item in beritaLainnya"
                    :key="item.id"
                    class="group flex gap-3"
                  >
                    <!-- Thumbnail -->
                    <Link
                      :href="`/read/${item.slug}`"
                      class="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100 block"
                    >
                      <img
                        v-if="getSidebarImage(item)"
                        :src="getSidebarImage(item)"
                        :alt="item.title"
                        class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        @error="$event.target.style.display='none'"
                      />
                      <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
                        <i class="fa-regular fa-image text-lg"></i>
                      </div>
                    </Link>
                    <!-- Text -->
                    <div class="flex-1 min-w-0">
                      <Link
                        :href="`/read/${item.slug}`"
                        class="text-sm font-medium text-gray-800 leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors"
                      >
                        {{ item.title }}
                      </Link>
                      <p class="mt-1 text-xs text-gray-400 flex items-center gap-1">
                        <i class="fa-regular fa-calendar"></i>
                        {{ formatTanggal(item.publish_date) }}
                      </p>
                    </div>
                  </li>
                  <li v-if="!beritaLainnya.length" class="text-sm text-gray-400 text-center py-4">
                    Tidak ada berita lain.
                  </li>
                </ul>
              </div>

            </aside>
          </div>
        </div>
      </main>
    </template>

    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { defineProps, ref, computed, onMounted } from "vue";
import { Link, Head, usePage } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";
import dayjs from "dayjs";
import "dayjs/locale/id";
dayjs.locale("id");
const props = defineProps({
  news: {
    type: Object,
    required: true,
  },
  otherNews: {
    type: [Array, Object],
    default: () => [],
  },
});
const formatTanggal = (tanggal) => {
  return tanggal ? dayjs(tanggal).format("D MMMM YYYY") : null;
};
const search = ref("");

// Base URL for absolute links (SSR-safe best effort)
const baseUrl = ref("");
onMounted(() => {
  if (typeof window !== "undefined" && window.location) {
    baseUrl.value = window.location.origin;
  }
});

// Canonical URL (path from Inertia + origin when available)
const page = usePage();
const canonicalUrl = computed(() => {
  const path = page.url?.split("#")[0] || "/";
  // Remove query params for canonical
  const cleanPath = path.split("?")[0];
  return baseUrl.value ? `${baseUrl.value}${cleanPath}` : cleanPath;
});

// Generate description from content if excerpt is missing
const stripHtml = (html) =>
  (html || "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const description = computed(() => {
  const raw =
    props.news?.data && (props.news.data.excerpt || props.news.data.summary)
      ? props.news.data.excerpt || props.news.data.summary
      : stripHtml(props.news?.data?.content).slice(0, 180);
  return raw;
});

// Determine primary image from field or first image in content; ensure absolute URL
const resolveAbsolute = (src) => {
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  if (baseUrl.value) {
    if (src.startsWith("/")) return `${baseUrl.value}${src}`;
    return `${baseUrl.value}/${src}`;
  }
  return src; // fallback
};

const primaryImage = computed(() => {
  const imgField = props.news?.data?.image;
  let src = "";
  if (Array.isArray(imgField) && imgField.length) {
    src = imgField[0];
  } else if (typeof imgField === "string" && imgField.length) {
    src = imgField;
  } else if (typeof props.news?.data?.content === "string") {
    const match = props.news.data.content.match(
      /<img [^>]*src=["']([^"']+)["'][^>]*>/i
    );
    if (match && match[1]) src = match[1];
  }
  return resolveAbsolute(src);
});

// Share URLs (use server-rendered share page for better previews)
const sharePath = computed(() => `/share/${props.news?.data?.slug || ""}`);
const absoluteShareUrl = computed(() =>
  baseUrl.value ? `${baseUrl.value}${sharePath.value}` : sharePath.value
);
const facebookShareUrl = computed(() => {
  const url = encodeURIComponent(absoluteShareUrl.value);
  return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
});
const whatsappShareUrl = computed(() => {
  const text = encodeURIComponent(
    `${props.news?.data?.title || ""} ${absoluteShareUrl.value}`.trim()
  );
  return `https://wa.me/?text=${text}`;
});

const beritaLainnya = computed(() => {
  const arr = Array.isArray(props.otherNews)
    ? props.otherNews
    : Array.isArray(props.otherNews?.data)
    ? props.otherNews.data
    : [];
  const list = arr.filter((item) => item.id !== props.news.data.id);
  if (!search.value) return list;
  return list.filter((item) =>
    item.title.toLowerCase().includes(search.value.toLowerCase())
  );
});

// Hero image for the article (first image from array or string)
const heroImage = computed(() => {
  const img = props.news?.data?.image;
  if (Array.isArray(img) && img.length) return img[0];
  if (typeof img === "string" && img) return img;
  return null;
});

// Thumbnail for sidebar items
const getSidebarImage = (item) => {
  if (Array.isArray(item.image) && item.image.length) return item.image[0];
  if (typeof item.image === "string" && item.image) return item.image;
  return null;
};
</script>

<style scoped>
/* ── Quill alignment ── */
.news-detail-content :deep(.ql-align-justify) { text-align: justify; }
.news-detail-content :deep(.ql-align-center)  { text-align: center; }
.news-detail-content :deep(.ql-align-right)   { text-align: right; }
.news-detail-content :deep(.ql-align-left)    { text-align: left; }

/* ── Typography ── */
.news-detail-content :deep(p) {
  margin: 0.75rem 0;
  line-height: 1.75;
  color: #374151;
  text-align: justify;
}

.news-detail-content :deep(h1),
.news-detail-content :deep(h2),
.news-detail-content :deep(h3),
.news-detail-content :deep(h4) {
  font-weight: 700;
  margin: 1.5rem 0 0.5rem;
  color: #111827;
  line-height: 1.3;
}

.news-detail-content :deep(h1) { font-size: 1.5rem; }
.news-detail-content :deep(h2) { font-size: 1.25rem; }
.news-detail-content :deep(h3) { font-size: 1.125rem; }

.news-detail-content :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin: 1rem 0;
}
.news-detail-content :deep(ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 1rem 0;
}
.news-detail-content :deep(li) {
  margin-bottom: 0.5rem;
  line-height: 1.65;
}

.news-detail-content :deep(blockquote) {
  border-left: 3px solid #2563eb;
  padding: 0.5rem 1rem;
  margin: 1.25rem 0;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 0 0.375rem 0.375rem 0;
}

.news-detail-content :deep(strong) { font-weight: 600; }
.news-detail-content :deep(em)     { font-style: italic; }
.news-detail-content :deep(u)      { text-decoration: underline; }

.news-detail-content :deep(code) {
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

/* ── Images ── */
.news-detail-content :deep(img) {
  display: block;
  max-width: 100%;
  height: auto;
  border-radius: 0.5rem;
  margin: 1.25rem auto;
  box-shadow: 0 1px 4px rgba(0,0,0,.1);
}

.news-detail-content :deep(figure) {
  margin: 1.5rem 0;
  text-align: center;
}
.news-detail-content :deep(figcaption) {
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
  margin-top: 0.375rem;
}

/* ── Sidebar line-clamp ── */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
